import {Component, Inject, OnInit} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from '@angular/material/dialog';
import {take} from 'rxjs/operators';
import {LookupService} from '../../../../common/services/lookup.service';
import {SharedService} from '../../../../common/services/shared-service';
import {RulesEngineMetadataDTO} from '../../../models/RulesEngineMetadataDTO';
import {RulesEngineMetadataService} from '../../../services/rules-engine-metadata.service';

enum Controls {
  ApplicationName = 'applicationName',
  ApplicationUrl = 'applicationUrl',
  ApplicationVersion = 'applicationVersion',
}

@Component({
  selector: 'app-rules-engine-metadata-dialog',
  templateUrl: './add-rules-engine-metadata-dialog.component.html',
  styleUrls: ['../../../../../../styles/global/_add-view-edit-styles.scss']
})
export class AddRulesEngineMetadataDialogComponent implements OnInit {
  readonly controls = Controls;

  errorMessage = '';
  heading = 'Add Rules Engine Metadata';
  formGroup: FormGroup;
  isEditMode: boolean;

  private rulesEngineMetadataDTO: RulesEngineMetadataDTO;
  private loggedInUserName: string;

  constructor(@Inject(MAT_DIALOG_DATA) public data: { rulesEngineMetadataId: bigint },
              private dialog: MatDialog,
              private dialogRef: MatDialogRef<AddRulesEngineMetadataDialogComponent>,
              private formBuilder: FormBuilder,
              private lookupService: LookupService,
              private rulesEngineMetadataService: RulesEngineMetadataService,
              private sharedService: SharedService) {

    this.loggedInUserName = this.sharedService.getLoggedInUserName();
  }

  ngOnInit(): void {
    this.initLimitFormControls();

    const {rulesEngineMetadataId} = this.data;
    if (rulesEngineMetadataId) {
      this.isEditMode = true;
      this.heading = `Edit Rules Engine Metadata`;
      this.getRulesEngineMetadataBy(rulesEngineMetadataId);
    }
  }

  initLimitFormControls() {
    this.formGroup = this.formBuilder.group({
      [this.controls.ApplicationName]: new FormControl(null, [Validators.required]),
      [this.controls.ApplicationUrl]: new FormControl(null, [Validators.required]),
      [this.controls.ApplicationVersion]: new FormControl(null, [Validators.required])
    });
  }

  onSaveRulesEngineMetadata() {
    const rulesEngineMetadataDTOPayload = this.getRulesEngineMetadataDTOPayload();
    this.rulesEngineMetadataService
      .saveRulesEngineMetadata(rulesEngineMetadataDTOPayload)
      .pipe(
        take(1)
      )
      .subscribe((response: RulesEngineMetadataDTO | any) => {
          if (response) {
            if (this.isEditMode) {
              this.sharedService.openSnackBar(`Rules Engine Metadata edited successfully`, 'Dismiss');
            } else {
              this.sharedService.openSnackBar(`Rules Engine Metadata added successfully`, 'Dismiss');
            }
            this.resetState();
            this.dialogRef.close({isSaved: true});
          } else if (response && response.businessMessage) {
            this.sharedService.openSnackBar(`${response.businessMessage}`, 'Dismiss');
          }
        },
        (error) => {
          console.error('Failed to save Rules Engine Metadata ', error);
          this.sharedService.openErrorDialog(error.message);
        });
  }

  private getRulesEngineMetadataBy(rulesEngineMetadataId: bigint): void {
    this.rulesEngineMetadataService
      .getRulesEngineMetadataBy(rulesEngineMetadataId)
      .pipe(
        take(1)
      )
      .subscribe(
        (response: RulesEngineMetadataDTO) => {
          if (response) {
            this.rulesEngineMetadataDTO = response;
            this.setFormsForEdit(response);
          } else {
            this.sharedService.openSnackBar(`Edit Rules Engine Metadata by ${rulesEngineMetadataId} not found`, 'Dismiss');
          }
        },
        (errorResponse) => {
          const error = errorResponse.error;
          this.sharedService.openErrorDialog(error.businessMessage);
        }
      );
  }

  private getRulesEngineMetadataDTOPayload() {
    let payload: RulesEngineMetadataDTO;

    payload = {
      rulesEngineMetadataId: this.isEditMode ? this.rulesEngineMetadataDTO.rulesEngineMetadataId : null,
      applicationName: this.formGroup.get(this.controls.ApplicationName).value,
      applicationUrl: this.formGroup.get(this.controls.ApplicationUrl).value,
      applicationVersion: this.formGroup.get(this.controls.ApplicationVersion).value,
      created: this.isEditMode ? this.rulesEngineMetadataDTO.created : new Date(),
      createdBy: this.isEditMode ? this.rulesEngineMetadataDTO.createdBy : this.loggedInUserName,
      tenantId: this.isEditMode ? this.rulesEngineMetadataDTO.tenantId : null,
      updated: this.isEditMode ? new Date().getTime() : null,
      updatedBy: this.isEditMode ? this.loggedInUserName : null
    };

    return payload;
  }

  private setFormsForEdit(rulesEngineMetadataDTO: RulesEngineMetadataDTO) {
    const {applicationName, applicationUrl, applicationVersion} = rulesEngineMetadataDTO;

    this.heading = `Edit Rules Engine Metadata - ${applicationName}`;
    this.formGroup.patchValue({
      applicationName,
      applicationUrl,
      applicationVersion
    });
  }

  private resetState() {
    this.rulesEngineMetadataDTO = null;
    this.formGroup.reset();
  }
}
